// Mano del jugador como CARTAS en HTML (separada del tablero 3D).
// Las fichas de la mano NO se dibujan sobre la mesa: se sostienen como cartas.
// Al arrastrar una carta sobre el tablero, se coloca allí (App.dropFromHand).
import { EVENTS } from '../utils/constants.js';

const TYPE_LABEL = { letter: '', syllable: 'SÍL', prefix: 'PRE', suffix: 'SUF', wildcard: '★' };

// Debe coincidir con el tamaño de las fichas del tablero (misma escala visual).
const PPU = 58;          // píxeles por unidad (igual que SceneManager.PPU)
const UNIT = 0.95;       // ancho base de una ficha (igual que GameScene UNIT)
/** Factor de ancho (unidades) de una ficha. El píxel/unidad real lo pone el CSS
 *  (var --hand-ppu) para poder reescalar la mano en móvil apaisado. */
function cardWidthFactor(widthUnits) {
  return (UNIT + (Math.max(1, widthUnits || 1) - 1) * 0.72).toFixed(3);
}

export class HandPanel {
  constructor(root, { bus, game, onDropToBoard, isInteractive, toast }) {
    this.bus = bus;
    this.game = game;
    this.onDropToBoard = onDropToBoard;
    this.isInteractive = isInteractive;
    this.toast = toast || (() => {});

    this.el = document.createElement('div');
    this.el.className = 'hand-panel hidden';
    this.el.setAttribute('aria-label', 'Tu mano');
    root.appendChild(this.el);

    this._drag = null;
    this._ghost = null;
    this._tray = null;
    this._trayOffset = { x: 0, y: 0 }; // desplazamiento de la bandeja de la mano
    this._onMove = (e) => this._pointerMove(e);
    this._onUp = (e) => this._pointerUp(e);
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('pointercancel', this._onUp);

    this._offState = this.bus.on(EVENTS.STATE_CHANGED, () => this.render());
    this._offTurn = this.bus.on(EVENTS.TURN_START, () => this.render());
    this._offBoard = this.bus.on(EVENTS.BOARD_CHANGED, () => this.render());
  }

  /** Jugador local (su mano es la única visible). */
  _human() {
    return this.game.selfPlayer || this.game.players?.find((p) => !p.isAI) || this.game.players?.[0];
  }

  /** Borde superior de la mano (para que el tablero 3D detecte soltar aquí). */
  topY() {
    return this.el.getBoundingClientRect().top;
  }

  show() { this.el.classList.remove('hidden'); this.render(); }
  hide() { this.el.classList.add('hidden'); }

  render() {
    if (this.el.classList.contains('hidden') || !this.game.state) return;
    const human = this._human();
    if (!human) return;
    const tiles = human.rack.tiles;
    const interactive = this.isInteractive();

    this.el.innerHTML = `
      <div class="hand-inner">
        <button class="hand-move" title="Mover la mano" aria-label="Mover la mano">⠿</button>
        <div class="hand-label">Tu mano · ${tiles.length} fichas</div>
        <div class="hand-cards">
          ${tiles.map((t) => this._cardHTML(t)).join('') || '<span class="hand-empty">Sin fichas</span>'}
        </div>
      </div>`;
    this.el.classList.toggle('inactive', !interactive);
    this.el.style.transform = `translate(${this._trayOffset.x}px, ${this._trayOffset.y}px)`;

    for (const card of this.el.querySelectorAll('.hand-card')) {
      card.addEventListener('pointerdown', (e) => this._pointerDown(e, card));
    }
    const handle = this.el.querySelector('.hand-move');
    handle.addEventListener('pointerdown', (e) => this._startTrayDrag(e));
  }

  _startTrayDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    this._tray = { sx: e.clientX, sy: e.clientY, bx: this._trayOffset.x, by: this._trayOffset.y };
  }

  /**
   * Acota el desplazamiento de la bandeja para que las CARTAS (.hand-inner) queden SIEMPRE
   * completas dentro de la ventana, con un margen extra arriba/abajo para no invadir las
   * zonas de gestos del sistema (barra de estado / barra de inicio) y salir de la app.
   */
  _clampTray(x, y) {
    const inner = this.el.querySelector('.hand-inner') || this.el;
    const r = inner.getBoundingClientRect();
    const baseLeft = r.left - this._trayOffset.x;
    const baseTop = r.top - this._trayOffset.y;
    const w = r.width, h = r.height;
    const vw = window.innerWidth || 1280;
    const vh = window.innerHeight || 720;
    const mX = 6;   // margen lateral
    const mTop = 44;    // margen superior (fuera de la zona de notificaciones)
    const mBot = 2;     // margen inferior (coincide con el reposo de la mano)
    const minX = mX - baseLeft;
    const maxX = vw - mX - w - baseLeft;
    const minY = mTop - baseTop;
    const maxY = vh - mBot - h - baseTop;
    const cl = (v, lo, hi) => (lo > hi ? Math.min(Math.max(v, hi), lo) : Math.max(lo, Math.min(hi, v)));
    return { x: cl(x, minX, maxX), y: cl(y, minY, maxY) };
  }

  _cardHTML(tile) {
    const text = tile.assignedText || tile.text;
    const label = TYPE_LABEL[tile.type] || '';
    const long = Array.from(text).length > 2;
    const wf = cardWidthFactor(tile.widthUnits);
    return `<div class="hand-card type-${tile.type}" data-id="${tile.instanceId}" style="--tile-col:${tile.color};width:calc(var(--hand-ppu, ${PPU}px) * ${wf})">
      ${label ? `<span class="hc-type">${label}</span>` : ''}
      <span class="hc-text${long ? ' long' : ''}">${text}</span>
      ${tile.type !== 'wildcard' ? `<span class="hc-val">${tile.value}</span>` : ''}
    </div>`;
  }

  _pointerDown(e, card) {
    if (!this.isInteractive()) return;
    e.preventDefault();
    const id = card.getAttribute('data-id');
    this._drag = { id, card, startX: e.clientX, startY: e.clientY, moved: false };
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'pick' });
  }

  _pointerMove(e) {
    // Durante un arrastre, evita que el navegador/SO lo interprete como gesto (scroll, barras).
    if ((this._tray || this._drag) && e.cancelable) e.preventDefault();
    // Mover toda la bandeja de la mano (acotada para NO salir de la pantalla).
    if (this._tray) {
      const rawX = this._tray.bx + (e.clientX - this._tray.sx);
      const rawY = this._tray.by + (e.clientY - this._tray.sy);
      this._trayOffset = this._clampTray(rawX, rawY);
      this.el.style.transform = `translate(${this._trayOffset.x}px, ${this._trayOffset.y}px)`;
      return;
    }
    if (!this._drag) return;
    const dx = e.clientX - this._drag.startX;
    const dy = e.clientY - this._drag.startY;
    if (!this._drag.moved && Math.hypot(dx, dy) > 6) {
      this._drag.moved = true;
      this._createGhost();
      this._drag.card.classList.add('dragging');
    }
    if (this._ghost) {
      this._ghost.style.left = `${e.clientX}px`;
      this._ghost.style.top = `${e.clientY}px`;
    }
  }

  _createGhost() {
    const g = this._drag.card.cloneNode(true);
    g.className = 'hand-card hand-ghost';
    g.style.setProperty('--tile-col', this._drag.card.style.getPropertyValue('--tile-col'));
    document.body.appendChild(g);
    this._ghost = g;
  }

  _pointerUp(e) {
    if (this._tray) { this._tray = null; return; }
    if (!this._drag) return;
    const drag = this._drag;
    this._drag = null;
    if (this._ghost) { this._ghost.remove(); this._ghost = null; }
    if (drag.card) drag.card.classList.remove('dragging');
    if (!drag.moved) { this._explainTile(drag.id); return; } // fue un toque, no un arrastre

    // ¿Se soltó SOBRE el tablero (por encima de la mano)? -> colocar la ficha allí.
    const handTop = this.topY();
    if (e.clientY < handTop - 4) {
      this.onDropToBoard(drag.id, e.clientX, e.clientY);
    } else {
      // Dentro de la mano -> REORDENAR la carta a la nueva posición.
      this._reorder(drag.id, e.clientX);
    }
  }

  /** Toque simple sobre una carta: si es COMODÍN, explica para qué sirve. */
  _explainTile(id) {
    const tile = this._human()?.rack?.tiles?.find((t) => t.instanceId === id);
    if (!tile || tile.type !== 'wildcard') return;
    const hints = {
      universal: 'Comodín universal: arrástralo al tablero y elige la letra o fragmento que quieras.',
      split: 'Comodín dividir: arrástralo y toca una ficha de la mesa para partirla en letras.',
      merge: 'Comodín unir: arrástralo y toca dos fichas contiguas para fusionarlas.',
      transform: 'Comodín transformar: arrástralo y toca una ficha de la mesa para cambiarla por otra.',
    };
    const msg = hints[tile.wildKind] || tile.description || 'Comodín: pieza especial. Arrástralo al tablero para usarlo.';
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'pick' });
    this.toast(msg, { icon: '✨', duration: 1600, center: true });
  }

  /** Reordena una carta dentro de la mano según la posición horizontal de soltado. */
  _reorder(id, clientX) {
    const rack = this._human()?.rack?.tiles;
    if (!rack) return;
    const cards = [...this.el.querySelectorAll('.hand-card')];
    let index = 0;
    for (const c of cards) {
      if (c.getAttribute('data-id') === id) continue;
      const r = c.getBoundingClientRect();
      if (clientX > r.left + r.width / 2) index++;
    }
    const from = rack.findIndex((t) => t.instanceId === id);
    if (from === -1) return;
    const [tile] = rack.splice(from, 1);
    rack.splice(index, 0, tile);
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'drop' });
    this.render();
  }

  dispose() {
    window.removeEventListener('pointermove', this._onMove);
    window.removeEventListener('pointerup', this._onUp);
    window.removeEventListener('pointercancel', this._onUp);
    this._offState?.();
    this._offTurn?.();
    this._offBoard?.();
    this._ghost?.remove();
  }
}
